import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import PageHeading from "../../components/common/PageHeading";
import SpringContainer from "../../components/containers/SpringContainer";
import Button from "../../components/form/Button";
import Input from "../../components/form/Input";
import LoadingSpinner from "../../components/loaders/LoadingSpinner";
import Table from "../../components/table/Table";

import {
  PencilSquareIcon,
  TagIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import ConfirmationModal from "../../components/features/ConfirmationModal";
import { usePagination } from "../../hooks/usePagination";
import useSelectedAccount from "../../hooks/useSelectedAccount";
import {
  useDeleteAccountTagMutation,
  useGetAccountTagsQuery,
} from "../../redux/services/accountTags";
import { formatDateUS } from "../../utils";
import AccountTagModal from "./AccountTagModal";

const limit = 20;

const AccountTags = () => {
  const { accountId } = useSelectedAccount();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<{
    accountTagId: string;
    name: string;
    description: string;
  } | null>(null);

  const [confirmDeleteTag, setConfirmDeleteTag] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { data, isLoading, isFetching, refetch, isUninitialized } =
    useGetAccountTagsQuery(
      {
        accountId,
        offset,
        limit,
        search: debouncedSearch,
        includeTotalCount: true,
      },
      { skip: !accountId }
    );

  const [deleteAccountTag] = useDeleteAccountTagMutation();

  useEffect(() => {
    // refetch();
  }, [refetch, accountId, offset]);

  const tags = data?.data || [];
  const totalCount = data?.totalCount || 0;
  const hasTags = tags.length > 0;

  const { paginationData } = usePagination({
    total: totalCount,
    offset,
    setOffset,
    limit,
    showPagination: totalCount > limit,
  });

  const handleConfirmDelete = async () => {
    if (!confirmDeleteTag) return;
    try {
      await deleteAccountTag({
        accountId,
        accountTagId: confirmDeleteTag.id,
      }).unwrap();
      toast.success("Tag deleted");
      refetch();
    } catch {
      toast.error("Failed to delete tag");
    } finally {
      setConfirmDeleteTag(null);
    }
  };

  const columns = [
    {
      key: "name",
      title: "Tag Name",
      headerClassName: "text-left w-[30%]",
      render: (tag: any) => <span>{tag.name}</span>,
    },
    {
      key: "description",
      title: "Description",
      headerClassName: "text-left w-[50%]",
      cellClassName: "max-w-96 text-wrap leading-5",
      render: (tag: any) => <span>{tag.description || "-"}</span>,
    },
    {
      key: "createdAt",
      title: "Created",
      render: (tag: any) => {
        return (
          <div className="md:text-center">{formatDateUS(tag.createdAt)}</div>
        );
      },
    },
    {
      key: "actions",
      title: "",
      render: (tag: any) => (
        <div className="text-right space-x-2">
          <div className="flex items-center justify-end gap-1">
            <button
              className="text-primary/90 hover:text-primary text-xs uppercase text-left p-1 hover:bg-primary/10 rounded"
              onClick={(e) => {
                e.stopPropagation();
                setEditingTag({
                  accountTagId: tag.accountTagId,
                  name: tag.name,
                  description: tag.description,
                });
                setIsModalOpen(true);
              }}
            >
              <PencilSquareIcon className="w-4 h-4" />
            </button>
            <div className="w-px h-4 bg-gray-200" />
            <button
              className="text-red-400 hover:text-red-600 text-xs uppercase text-left p-1 hover:bg-red-600/10 rounded"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDeleteTag({ id: tag.accountTagId, name: tag.name });
              }}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      ),
    },
  ];

  if (isUninitialized || isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <LoadingSpinner size="32px" />
      </div>
    );
  }

  return (
    <SpringContainer>
      <div className="bg-white h-full">
        <div className="flex justify-between items-center md:mb-4">
          <div className="flex flex-col lg:flex-row gap-2 lg:items-center">
            <PageHeading
              heading="Account Tags"
              tooltip="Refresh"
              loading={isFetching}
              refetch={hasTags ? refetch : undefined}
            />
            {hasTags && (
              <div className="hidden md:block">
                <Input
                  search
                  value={search}
                  placeholder="Search tags"
                  onChange={(e) => setSearch(e.target.value)}
                  onBlur={() => setDebouncedSearch(search)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Tab") {
                      setDebouncedSearch(search);
                    }
                  }}
                  loading={isFetching}
                  width={350}
                />
              </div>
            )}
          </div>
          {hasTags && (
            <Button
              variant="primary"
              onClick={() => setIsModalOpen(true)}
            >
              New
            </Button>
          )}
        </div>

        {hasTags && (
          <div className="block md:hidden my-2 mb-4">
            <Input
              search
              value={search}
              placeholder="Search tags"
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => setDebouncedSearch(search)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Tab") {
                  setDebouncedSearch(search);
                }
              }}
            />
          </div>
        )}

        {!hasTags && !debouncedSearch && (
          <div className="flex flex-col items-center justify-center text-center py-16">
            <div className="mb-4 text-gray-900 bg-gray-300 rounded-full p-4 flex justify-center items-center">
              <TagIcon className="h-10 w-10" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Account Tags
            </h2>
            <p className="text-sm text-gray-600 max-w-md">
              Tags help you group and search your templates and articles. Create
              your first tag to get started.
            </p>
            <div className="mt-6">
              <Button
                variant="primary"
                onClick={() => setIsModalOpen(true)}
              >
                + Create Tag
              </Button>
            </div>
          </div>
        )}
        {!hasTags && debouncedSearch && (
          <div className="text-sm text-gray-700">No matching tags found.</div>
        )}
        {hasTags && (
          <Table<any>
            columns={columns}
            data={tags}
            keyExtractor={(tag) => tag.accountTagId}
            // highlight the editing row
            selectedRowKey={editingTag?.accountTagId}
            // open modal in edit mode on row click
            onRowClick={(tag) => {
              setEditingTag({
                accountTagId: tag.accountTagId,
                name: tag.name,
                description: tag.description,
              });
              setIsModalOpen(true);
            }}
            paginationData={paginationData}
          />
        )}
        <AccountTagModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTag(null);
          }}
          accountId={accountId!}
          onSuccess={() => {
            refetch();
            setEditingTag(null);
          }}
          initialName={editingTag?.name}
          initialDescription={editingTag?.description}
          tagId={editingTag?.accountTagId}
        />

        <ConfirmationModal
          isOpen={!!confirmDeleteTag}
          onRequestClose={() => setConfirmDeleteTag(null)}
          onConfirm={handleConfirmDelete}
          title="Delete Tag?"
          message={
            <span>
              Are you sure you want to delete the tag{" "}
              <strong>{confirmDeleteTag?.name}</strong>?
            </span>
          }
          confirmText="Delete"
          cancelText="Cancel"
        />
      </div>
    </SpringContainer>
  );
};

export default AccountTags;
